import { createActivityCommonStore, createSessionStore } from "@/app/activity/store"
import Dialog from "@/components/dialog/Dialog"
import {
  useCreateActivityDataMutation,
  useDeleteActivityDataMutation,
} from "@/hooks/api/useActivityQuery"
import { useQueryClient } from "@tanstack/react-query"
import { uniqBy } from "lodash"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import "./playerDeleteConfirmDialog.scss"
import BasicButton from "@/components/button/BasicButton"

export default function PlayerDeleteConfirmDialog() {
  const { t } = useTranslation()

  const { mutateAsync: addToActivity } = useCreateActivityDataMutation()
  const { mutateAsync: removeFromActivity } = useDeleteActivityDataMutation()

  const client = useQueryClient()

  const { playerRemoveDialogProps, setPlayerRemoveDialogProps } = createActivityCommonStore(
    (state) => ({
      playerRemoveDialogProps: state.playerRemoveDialogProps,
      setPlayerRemoveDialogProps: state.setPlayerRemoveDialogProps,
    }),
  )
  const addToActivityPayload = useMemo(() => {
    const { idfActivity, idfData } = playerRemoveDialogProps || {}

    return {
      idfActivity: idfActivity as number,
      payload: { idfData },
    }
  }, [playerRemoveDialogProps])

  const participatedSessionList = useMemo(() => {
    if (!playerRemoveDialogProps?.sessionList) {
      return []
    }

    return uniqBy(playerRemoveDialogProps.sessionList, "idfSession")
  }, [playerRemoveDialogProps])

  const { sessions } = createSessionStore((state) => ({
    sessions: state.sessions,
  }))

  const [isOpen, setIsOpen] = useState(false)

  const closeDialog = () => {
    setIsOpen(false)
    invalidateQueries()
    setPlayerRemoveDialogProps(undefined)
  }

  const handleCreateActivityData = async () => {
    try {
      await addToActivity(addToActivityPayload)
      invalidateQueries()
      setPlayerRemoveDialogProps(undefined)
    } catch (error) {
      console.error(error)
    }
  }

  const handleRemoveFromActivity = async () => {
    try {
      const { idfActivity, idfData } = playerRemoveDialogProps || {}
      await removeFromActivity({ idfActivity: idfActivity as number, idfData: idfData as number })

      if (isOpen) {
        closeDialog()
      } else {
        invalidateQueries()
        setPlayerRemoveDialogProps(undefined)
      }
    } catch (error) {
      console.error(error)
    }
  }

  const invalidateQueries = () => {
    const { idfActivity } = playerRemoveDialogProps || {}

    client.invalidateQueries({ queryKey: ["readActivityData", idfActivity] })
    client.invalidateQueries({ queryKey: ["readActivity", idfActivity] })
    client.invalidateQueries({ queryKey: ["readPlayersRpe", idfActivity] })
    client.invalidateQueries({ queryKey: ["readSessionList"] })
    // readSessionDataItems는 이 선수가 참여했던 세션만 targeted invalidate
    // (broad invalidate 시 세션 N개의 쿼리가 동시 refetch되어 page.tsx의
    //  스토어 동기화 useEffect가 setState 연쇄를 일으킴 — Maximum update depth 크래시)
    participatedSessionList.forEach(({ idfSession }) => {
      client.invalidateQueries({ queryKey: ["readSessionDataItems", idfSession] })
    })
  }

  useEffect(() => {
    if (!playerRemoveDialogProps) {
      return
    }

    if (playerRemoveDialogProps?.sessionList?.length) {
      setIsOpen(true)

      return
    }

    if (playerRemoveDialogProps?.isMatch) {
      handleCreateActivityData()
    } else {
      handleRemoveFromActivity()
    }
  }, [playerRemoveDialogProps])

  return (
    <Dialog
      open={isOpen}
      title={t("removeFromActivity")}
      onClose={closeDialog}
      maxWidth="600px"
      footer={
        <>
          <BasicButton size="md" type="default-outlined" onClick={closeDialog}>
            {t("button.cancel")}
          </BasicButton>
          <BasicButton size="md" type="danger-outlined" onClick={handleRemoveFromActivity}>
            {t("button.remove")}
          </BasicButton>
        </>
      }>
      <div className="PlayerDeleteConfirmDialogCont">
        <p className="session-list-header">
          {playerRemoveDialogProps?.activityData?.devicePlayer?.nickname}
        </p>
        <div className="session-list">
          {participatedSessionList.map((session) => {
            const sessionData = sessions.find((s) => s.idfSession === session.idfSession)

            if (!sessionData) {
              return null
            }

            return (
              <p key={session.idfSession || new Date().valueOf()} className="session-name">
                {sessionData?.name}
              </p>
            )
          })}
        </div>
        <p className="confirm-desc">{t("removeFromActivityDialogContent")}</p>
      </div>
    </Dialog>
  )
}
